import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useEnv } from 'src/app/components/authorization/environment-hooks'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faAtomSimple,
  faCalendarDays,
  faCalendarRange,
  faCashRegister,
  faChartMixed,
  faCode,
  faCommentQuestion,
  faGear,
  faMailboxFlagUp,
  faMoneyBillWave,
  faObjectsColumn,
  faPaperPlaneTop,
  faRectangleHistory,
  faUserGroup,
  faCalendarClock,
} from '@fortawesome/pro-regular-svg-icons'
import { ReservationsListParams } from 'src/app/pages/booking/reservation-list/reservation-list.page'
import { useHourlyResourceTypeCheck } from '../hourly-resource-type/hourly-resource-type'

export type MenuItem =
  | {
      key: string
      description: string
      icon: React.ReactElement
      route: string
      dev: true
    }
  | {
      key: string
      description: string
      icon: React.ReactElement
      route: string
    }

export const useMenu = (tenantId: string, propertyId: string): { menu: MenuItem[]; hourlyResourceTypeExists: boolean } => {
  const { t } = useTranslation()
  const { isDevEnv } = useEnv()

  const hourlyResourceTypeExists = useHourlyResourceTypeCheck(tenantId, propertyId)

  const menuItems = useMemo(
    () =>
      [
        {
          key: 'DASHBOARD',
          description: t('pms.menu.dashboard'),
          icon: <FontAwesomeIcon icon={faObjectsColumn} />,
          route: '/dashboard',
        },
        {
          key: 'PLANNING',
          description: t('pms.menu.planning'),
          icon: <FontAwesomeIcon icon={faCalendarRange} />,
          route: '/booking/planning',
        },
        {
          key: 'TIME_PLANNING',
          description: t('pms.menu.time_planning'),
          icon: <FontAwesomeIcon icon={faCalendarClock} />,
          route: '/booking/planning-hourly',
        },
        {
          key: 'QUOTATIONS',
          description: t('pms.menu.quotation'),
          icon: <FontAwesomeIcon icon={faPaperPlaneTop} />,
          route: '/booking/quotations-list',
        },
        {
          key: 'RESERVATIONS',
          description: t('pms.menu.reservations'),
          icon: <FontAwesomeIcon icon={faRectangleHistory} />,
          route: `/booking/reservations/${ReservationsListParams.LIST}`,
        },
        {
          key: 'MESSAGES',
          description: t('pms.menu.messages'),
          icon: <FontAwesomeIcon icon={faMailboxFlagUp} />,
          route: '/messages',
        },
        {
          key: 'POS',
          description: t('pms.menu.pos'),
          icon: <FontAwesomeIcon icon={faCashRegister} />,
          route: '/booking/pos',
        },
        {
          key: 'ACCOUNTING',
          description: t('pms.menu.accounting'),
          icon: <FontAwesomeIcon icon={faMoneyBillWave} />,
          route: '/accounting/property/documents',
        },
        {
          key: 'CALENDAR',
          description: t('pms.menu.calendar'),
          icon: <FontAwesomeIcon icon={faCalendarDays} />,
          route: '/booking/prices-calendar',
        },
        {
          key: 'SINAPSI',
          description: t('pms.menu.sinapsi'),
          icon: <FontAwesomeIcon icon={faAtomSimple} />,
          route: '/sinapsi',
        },
        {
          key: 'STATISTICS',
          description: t('pms.menu.stats'),
          icon: <FontAwesomeIcon icon={faChartMixed} />,
          route: '/common/statistics',
        },
        {
          key: 'CUSTOMERS',
          description: t('pms.menu.customers'),
          icon: <FontAwesomeIcon icon={faUserGroup} />,
          route: '/common/customer',
        },
        {
          key: 'SETTINGS',
          description: t('pms.menu.settings'),
          icon: <FontAwesomeIcon icon={faGear} />,
          route: '/settings',
        },
        {
          key: 'SUPPORT',
          description: t('pms.menu.support'),
          icon: <FontAwesomeIcon icon={faCommentQuestion} />,
          route: '/support',
        },
        {
          key: 'DEVELOP',
          description: t('pms.menu.develop'),
          icon: <FontAwesomeIcon icon={faCode} />,
          route: '/develop',
          dev: true as const,
        },
      ].filter((i) => (hourlyResourceTypeExists ? i : i.key !== 'TIME_PLANNING')),
    [hourlyResourceTypeExists, tenantId, propertyId],
  )

  if (isDevEnv) return { menu: menuItems, hourlyResourceTypeExists }
  else return { menu: menuItems.filter((i) => !i.dev), hourlyResourceTypeExists }
}
